/**
 * WidgetSuggestionCard — inline card the chat surface renders when the chat
 * agent suggests the user ADOPT or CREATE a widget (the dedicated early-turn
 * nudge, distinct from LogCard which logs to an already-adopted widget).
 *
 * The chat agent emits it by calling `mcp_session_suggest_widget`; the runner
 * relays a `widget_suggestion` card carrying the resolved widget_id + the first
 * item to seed. Tapping **Create** instantiates the widget (a template → a new
 * named collection, a standard widget → adopted) AND logs that first item in
 * one atomic call (`POST /api/widget-instances` = instantiate-and-log), then
 * offers **Open** to jump into the new instance's data view.
 *
 * Single-create guard: templates create a NEW instance per call, so the button
 * disables while busy and stays in the "Created" state afterward. The minted
 * `log_card_id` makes the first data row idempotent (a stray retry 409s on the
 * server rather than silently double-logging).
 *
 * Props:
 *   widgetId    — required; the parent widget to instantiate.
 *   widgetKind  — 'template' | 'standard' (drives whether `title` is sent).
 *   title       — the collection/instance name to create (template only).
 *   widgetTitle — the parent widget's own title (display fallback).
 *   reason      — one-line plain-language why-this-fits, shown under the title.
 *   kind        — payload "kind" of the first item (e.g. "item", "merch").
 *   payload     — the first item to seed (logged on Create).
 *   sessionId   — source session for the first item (so the data view's
 *                 clock-jump can return here).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { instantiateAndLog, widgetInstances } from '../../services/widgetApi';

// Deterministic UUID from a string (FNV-1a over 4 salted passes → 32 hex, UUID
// format). Used to derive a STABLE log_card_id from the card's id so Create
// stays idempotent and the "Created" state can be recovered from the server
// after a reload (the data row's existence is the source of truth — no
// client-only flag that resets on refresh).
function stableUuid(str) {
  let hex = '';
  for (let salt = 0; salt < 4; salt++) {
    let h = (0x811c9dc5 ^ salt) >>> 0;
    const s = `${str}:${salt}`;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    hex += (h >>> 0).toString(16).padStart(8, '0');
  }
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

const STYLE = {
  card: {
    border: '1px solid #34C759',
    borderRadius: 12,
    padding: '12px 14px',
    background: '#F4FCF6',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxWidth: 420,
  },
  header: { display: 'flex', alignItems: 'center', gap: 8 },
  badge: {
    display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 999,
    fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    background: '#34C75920', color: '#1f8f43', whiteSpace: 'nowrap',
  },
  title: { flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, color: '#1C1C1E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  reason: { fontSize: 12.5, color: '#3C3C43', lineHeight: 1.45 },
  payload: { fontSize: 12, color: '#6b6b70', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  actions: { display: 'flex', gap: 8 },
  btn: {
    flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #34C759',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    // Animate the Create→✓Created flip + the tap (press-scale via the injected
    // `.wscard button:active` rule) so the card updates smoothly, not a hard swap.
    transition: 'opacity 0.15s, background-color 0.18s, border-color 0.18s, color 0.18s, transform 0.09s ease-out',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  },
  btnPrimary: { background: '#34C759', color: '#FFFFFF', borderColor: '#34C759' },
  btnCreated: { background: '#E5E7E5', color: '#1f8f43', borderColor: '#34C759', cursor: 'default' },
  btnOpen: { background: 'transparent', color: '#1f8f43' },
  spinner: {
    width: 12, height: 12, border: '2px solid #FFFFFF44', borderTopColor: '#FFFFFF',
    borderRadius: '50%', animation: 'wscard-spin 0.7s linear infinite',
  },
};

export default function WidgetSuggestionCard({
  cardId = null,
  logCardId: serverLogCardId = null,
  widgetId,
  widgetKind,
  title,
  widgetTitle,
  reason,
  kind,
  payload,
  logOptions = null,
  sessionId = null,
}) {
  const navigate = useNavigate();
  const [created, setCreated] = useState(false);
  const [busy, setBusy] = useState(false);
  // Which option index is mid-commit, so ONLY the tapped option shows a spinner.
  const [busyOption, setBusyOption] = useState(-1);
  const [error, setError] = useState(null);
  const [instanceId, setInstanceId] = useState(null);
  // When the widget declares log_options, Create must STOP and let the user
  // pick the bucket for the first item (no autonomous direct-log). `optionStage`
  // = the instance exists and we're waiting for that pick.
  const [optionStage, setOptionStage] = useState(false);
  const [loggedOption, setLoggedOption] = useState(null);
  const mounted = useRef(true);
  const optionList = Array.isArray(logOptions?.options) ? logOptions.options : null;
  const hasFirstItem = !!(payload && typeof payload === 'object' && Object.keys(payload).length > 0);
  // Idempotency key. PREFER the server-assigned `log_card_id` (the one
  // authoritative key the server reconciles on + both clients share). Fall back
  // to the legacy cardId hash for OLD cards persisted before the server minted
  // one, then to a random id for a card with no id at all.
  const logCardId = useMemo(
    () => (serverLogCardId
      || (cardId
        ? stableUuid(`wsc:${cardId}`)
        : (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `wsc-${Date.now()}-${Math.round(Math.random() * 1e9)}`))),
    [serverLogCardId, cardId],
  );

  // Restore "Created" after a reload: if a data row with this stable log_card_id
  // already exists, the suggestion was already actioned — show Created/Open
  // instead of the Create button (Issue-B: the card must not revert to "Create
  // xxx" on refresh).
  useEffect(() => {
    mounted.current = true;
    if (!logCardId) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await widgetInstances.list({ log_card_id: logCardId });
        const inst = (res?.instances || res || [])[0];
        if (alive && inst?.instance_id) {
          setInstanceId(inst.instance_id);
          setCreated(true);
        }
      } catch { /* best-effort — fall back to the Create button */ }
    })();
    return () => { alive = false; mounted.current = false; };
  }, [logCardId]);

  const displayTitle = title || widgetTitle || 'New widget';
  const isTemplate = widgetKind === 'template';

  const onCreate = useCallback(async () => {
    if (busy || created || !widgetId) return;
    setBusy(true);
    setError(null);
    try {
      // log_options + a first item → CREATE, then STOP for the user to pick the
      // bucket (the autonomous log must not direct-log a choice-bearing widget).
      if (optionList && optionList.length > 0 && hasFirstItem) {
        const inst = await widgetInstances.instantiate(widgetId, {
          title: isTemplate ? displayTitle : undefined,
        });
        if (!mounted.current) return;
        setInstanceId(inst?.instance_id || inst?.instance?.instance_id || null);
        setOptionStage(true);
        return;
      }
      if (hasFirstItem) {
        // Clear first item, no choices → create AND log it in one atomic call.
        const result = await instantiateAndLog({
          widget_id: widgetId,
          log_card_id: logCardId,
          kind: kind || 'item',
          payload,
          title: isTemplate ? displayTitle : undefined,
          source_session_id: sessionId,
        });
        if (!mounted.current) return;
        setInstanceId(result?.instance?.instance_id || null);
        setCreated(true);
        return;
      }
      // Create-only (no item to log yet) → just instantiate the widget shell.
      const inst = await widgetInstances.instantiate(widgetId, {
        title: isTemplate ? displayTitle : undefined,
      });
      if (!mounted.current) return;
      setInstanceId(inst?.instance_id || inst?.instance?.instance_id || null);
      setCreated(true);
    } catch (e) {
      if (mounted.current) setError(e?.message || String(e));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [busy, created, widgetId, optionList, hasFirstItem, payload, displayTitle, isTemplate, kind, logCardId, sessionId]);

  // Pick the bucket for the first item (log_options). A single pick = a single
  // row keyed by this card's own log_card_id (a valid UUID), with the option's
  // `set` patch merged in to route it to the right group.
  const pickOption = useCallback(async (i, opt) => {
    if (busy || created || !instanceId) return;
    setBusy(true);
    setBusyOption(i);
    setError(null);
    try {
      const optSet = (opt.set && typeof opt.set === 'object' && !Array.isArray(opt.set)) ? opt.set : {};
      await widgetInstances.appendData(instanceId, {
        kind: kind || 'item',
        payload: { ...payload, ...optSet, log_option: opt.prompt, log_option_label: opt.label },
        log_card_id: logCardId,
        source_session_id: sessionId,
      });
      if (!mounted.current) return;
      setLoggedOption(opt.label || opt.prompt || null);
      setOptionStage(false);
      setCreated(true);
    } catch (e) {
      if (mounted.current) setError(e?.message || String(e));
    } finally {
      if (mounted.current) { setBusy(false); setBusyOption(-1); }
    }
  }, [busy, created, instanceId, payload, kind, logCardId, sessionId]);

  const onOpen = useCallback(() => {
    if (instanceId) navigate(`/playground/widget/studio/instance/${instanceId}`);
    else navigate('/playground/widget/studio');
  }, [navigate, instanceId]);

  const summary = summarizePayload(payload);

  return (
    <div className="wscard" style={STYLE.card}>
      <style>{`@keyframes wscard-spin { to { transform: rotate(360deg); } } .wscard button:active:not(:disabled) { transform: scale(0.96); }`}</style>
      <div style={STYLE.header}>
        <span style={STYLE.badge}>{isTemplate ? 'New collection' : 'New widget'}</span>
        <span style={STYLE.title}>{displayTitle}</span>
      </div>
      {reason && <div style={STYLE.reason}>{reason}</div>}
      {summary && <div style={STYLE.payload}>First item: {summary}</div>}
      {optionStage && optionList ? (
        // Created — now the user picks where the first item lands (log_options).
        <>
          <div style={{ ...STYLE.payload, color: '#1f8f43', fontWeight: 600 }}>
            Created — where should “{summary || displayTitle}” go?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {optionList.map((opt, i) => (
              <button
                key={i}
                onClick={() => pickOption(i, opt)}
                disabled={busy}
                style={{ ...STYLE.btn, ...STYLE.btnPrimary, opacity: busy && busyOption !== i ? 0.6 : 1, justifyContent: 'flex-start' }}
              >
                {busyOption === i ? <span style={STYLE.spinner} /> : (opt.label || opt.prompt)}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div style={STYLE.actions}>
          {created ? (
            <>
              <button disabled style={{ ...STYLE.btn, ...STYLE.btnCreated }}>
                {loggedOption ? `✓ Logged to ${loggedOption}` : '✓ Created'}
              </button>
              <button onClick={onOpen} style={{ ...STYLE.btn, ...STYLE.btnOpen }}>Open</button>
            </>
          ) : (
            <button onClick={onCreate} disabled={busy} style={{ ...STYLE.btn, ...STYLE.btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy ? <span style={STYLE.spinner} /> : (isTemplate ? `Create "${displayTitle}"` : 'Add')}
            </button>
          )}
        </div>
      )}
      {error && <div style={{ fontSize: 11, color: '#FF3B30' }}>{error}</div>}
    </div>
  );
}

// One-line glance summary of the first item — best-effort, never throws.
function summarizePayload(payload) {
  if (!payload || typeof payload !== 'object') return '';
  if (typeof payload.summary === 'string') return payload.summary;
  if (typeof payload.name === 'string') {
    const extra = payload.kind && payload.kind !== payload.name ? ` · ${payload.kind}` : '';
    return `${payload.name}${extra}`;
  }
  try {
    const keys = Object.keys(payload).filter((k) => k !== 'image_url').slice(0, 3);
    return keys.map((k) => `${k}: ${JSON.stringify(payload[k])}`).join(' · ');
  } catch { return ''; }
}
